import React, { useEffect, useState, useRef } from 'react';
import "./InterstitialPage.scss"
import { Button } from '../components/button/Button';
import Loading from '../components/loading/Loading';
import { X, Headphones, CheckFat, ClockCountdown, SealPercent, ListChecks, LinkedinLogo, ArrowLeft } from '@phosphor-icons/react';

import { motion } from "motion/react"

interface InterstitialPageProps {
    setIsInterstitialVisible: (visible: boolean) => void;
    remainingTime: null | number;
    connectConversation: (hasResume: boolean) => void;
    profilePic: any;
    isLoading?: boolean;
}

export const InterstitialPage: React.FC<InterstitialPageProps> = ({
  setIsInterstitialVisible,
  remainingTime,
  connectConversation,
  profilePic,
  isLoading = false
}) => {

  const [selectedTime, setSelectedTime] = useState('15m'); // Default selection
  const [showSharePreview, setShowSharePreview] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const profileDisplay = profilePic && (
    <img src={profilePic} alt="Profile" />
  );

  // Mapping of time options to prices
  const timePrices: Record<string, string> = {
    '10m': '$5',
    '15m': '$7',
    '30m': '$12',
    '60m': '$20',
  };

  const handleTimeClick = (time: string) => {
    setSelectedTime(time);
  };

  const renderLoading = () => (
    <Loading status="Preparing your summary..." />
  );

  const renderReadyView = () => (
    <ReadyView
      setIsInterstitialVisible={setIsInterstitialVisible}
      connectConversation={connectConversation}
    />
  );

  const renderSharePreview = () => (
    <SharePreview
      setShowSharePreview={setShowSharePreview}
      profileDisplay={profileDisplay}
    />
  );

  const renderPaywall = () => (
    <div className="paywall">
      {/* <a
        href="#"
        className="close-button"
        onClick={() => setIsInterstitialVisible(false)}
      >
        <X weight="bold" size={16} />
      </a> */}

      <div className="interstitial-header">
        {/* <UserCircleCheck size={48} weight="duotone" /> */}
        <h3>Choose an option to continue</h3>
        <h5>How would you like to get credits?</h5>
      </div>

      <div className="paywall-content">

        <div className={`time-picker paywall-card`}>

          <div className="limited-offer"><SealPercent size={12} weight="fill" color="white" /> Limited time offer</div>
          <div className="paywall-card-header">
            Get 30 minutes for <span className="strike">$30</span> <span className="price">$10</span>
          </div>

          <ul>
            <li><CheckFat size={16} weight="fill" color="green" />Resume analysis</li>
            <li><CheckFat size={16} weight="fill" color="green" />Personalized interview</li>
            <li><CheckFat size={16} weight="fill" color="green" />AI feedback</li>
          </ul>

          <Button
            label="Checkout"
            buttonStyle="special"
            buttonSize="lg"
            onClick={() => {
              window.location.href = "/aiproxy/stripe_checkout";
            }}
          />

        </div>

        <div
          className={`share-card paywall-card`}>
          <div className="paywall-card-header">Free</div>

          <p>Share PracticeRun on LinkedIn to get <b>10 minutes</b> of interview time for <b>FREE</b>.</p>

          <Button
            label="Share"
            icon={LinkedinLogo}
            iconSize={16}
            iconPosition={'start'}
            iconWeight="fill"
            buttonStyle="accent"
            onClick={() => {
              // setShowSharePreview(true);
              window.location.href = "/aiproxy/start_linkedin_share_flow";
            }}
          />
        </div>


      </div>

      <div className="powered-by">Checkout powered by <img src="./img/stripe.png" alt="Stripe" /></div>

    </div>
  );

  const renderContent = () => {
    if (isLoading) return renderLoading();
    if (showSharePreview) return renderSharePreview();
    if (remainingTime == null || remainingTime <= 0) return renderPaywall();
    return renderReadyView();
  };


  return (
    <motion.div
      className="interstitial"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1}}
    >
      <motion.div
          className="interstitial-modal"
          initial={{ opacity: 0, scale: 0.9}}
          animate={{ opacity: 1, scale: 1}}
          transition={{
            opacity: { delay: 0.3 },
            scale: { type: "spring", stiffness: 200, damping: 20, delay: 0.3 },
          }}
      >

        {renderContent()}

        </motion.div>

    </motion.div>
  );


};

const ReadyView: React.FC<{
  setIsInterstitialVisible: (visible: boolean) => void;
  connectConversation: (hasResume: boolean) => void;
}> = ({ setIsInterstitialVisible, connectConversation }) => {

  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0] || null;
    setFile(uploadedFile);
  };

  const handleStartInterview = async () => {
    let hasResume = false;
    if (!file) {
      connectConversation(hasResume);
      setIsInterstitialVisible(false);
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append('resume', file);
    try {
      await fetch('/aiproxy/upload-resume', {
        method: 'POST',
        body: formData,
      });
      hasResume = true;
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setIsProcessing(false);
      setIsInterstitialVisible(false);
      connectConversation(hasResume);
    }
  };


  return (
    isProcessing ? (
      <Loading status='Preparing your interview...' />
    ) : (
      <div className="ready">
        <a href="#" className="close-button" onClick={() => setIsInterstitialVisible(false)}>
          <X weight="bold" size={16} />
        </a>

        <div className="interstitial-header">
          <h3>Add your resume</h3>
          <h5>This step is optional but helps the AI ask more specific questions.</h5>
        </div>

        <div className="file-upload">
          <input
            type="file"
            onChange={handleFileChange}
          />
        </div>

        <h5>Before you begin</h5>
        <ul>
          <li><Headphones size={16} weight="duotone" /> For best results find a quiet place or use headphones.</li>
          <li><ClockCountdown size={16} weight="duotone" /> The interview will last approximetly 5 - 10 minutes.</li>
          <li><ListChecks size={16} weight="duotone" /> When you finish the AI will evaluate how you did.</li>
        </ul>

        <Button
          label={ 'Start Interview' }
          buttonStyle={'primary'}
          onClick={ handleStartInterview }
        />
      </div>
    )
  );
};

const SharePreview: React.FC<{ setShowSharePreview: (show: boolean) => void, profileDisplay: JSX.Element }> = ({ setShowSharePreview, profileDisplay }) => (
  <div className="share-preview">
    <a href="#" className="back-button" onClick={() => setShowSharePreview(false)}>
      <ArrowLeft weight="bold" size={16} />
    </a>
    <h3>Share preview</h3>
    <div className="share-post">
      <div className="share-header">
        <div className="share-profile">
          {profileDisplay}
        </div>
        <div className="share-details">
          <div className="share-name">LinkedIn Name</div>
          <div className="share-meta">Meta details</div>
        </div>
      </div>
      <div className="share-text">
        Doing mock interviews with a realtime voice AI is a game changer. <a href="https://practicerun.ai">PracticeRun.ai</a>.
      </div>
      <div className="share-media">
        <img src="./img/share-image.png" alt="Share" />
      </div>
    </div>
    <Button
      label='Share'
      buttonSize='rg'
      buttonStyle='accent'
      icon={LinkedinLogo}
      iconSize={16}
      iconWeight='fill'
      onClick={() => {
        window.location.href = "/aiproxy/start_linkedin_share_flow";
      }}
    />
  </div>
);
